/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, useTheme } from '@mui/material';
import {
  APIProvider,
  Map,
  MapProps,
  useMap,
  AdvancedMarker,
} from '@vis.gl/react-google-maps';
import React, { ReactNode, useMemo, useEffect, useState, useRef } from 'react';
import { MapIcon, WMSInterface } from '@schamburg-web/interfaces';
import { useDomains, useModules, useAlert } from '../../contexts';
import {
  MapProvider,
  useMapContext,
} from '../../contexts/MapContext/MapContext';
import GoogleEndpointLayer from './GoogleEndpointLayer';
import GoogleInfoWindow from './GoogleInfoWindow';
import GoogleTrafficLayer from './GoogleTrafficLayer';
import GoogleWmsLayers from './GoogleWmsLayers';
import MapMarquee from '../MapMarquee/MapMarquee';
import MapMessage from '../MapMessage/MapMessage';

interface GoogleMapElementProps {
  apiKey: string;
  mapOptions: MapProps;
  children?: ReactNode;
  locationEnabled?: boolean; // Pass location state as prop
  setLocationEnabled: (enabled: boolean) => void;
}

const defaultMapOptions = {
  style: { width: '100%', height: '100%' },
  gestureHandling: 'greedy',
  disableDefaultUI: true,
  mapId: '',
};

const resolveWmsMapLayer = (
  layer: unknown,
  mode: 'light' | 'dark'
): WMSInterface | undefined => {
  if (!layer) return undefined;
  if (typeof layer === 'object' && 'server' in (layer as object)) {
    return layer as WMSInterface;
  }

  const themedLayer = layer as { light?: WMSInterface; dark?: WMSInterface };
  return mode === 'dark'
    ? themedLayer.dark ?? themedLayer.light
    : themedLayer.light ?? themedLayer.dark;
};

const GoogleMapElement: React.FC<GoogleMapElementProps> = ({
  mapOptions,
  apiKey,
  children,
  locationEnabled,
  setLocationEnabled,
}) => {
  return (
    <MapProvider>
      <APIProvider apiKey={apiKey}>
        <InnerGoogleMapElement
          mapOptions={mapOptions}
          locationEnabled={locationEnabled}
          setLocationEnabled={setLocationEnabled}
        >
          {children}
        </InnerGoogleMapElement>
      </APIProvider>
    </MapProvider>
  );
};

// Component to handle geolocation functionality
const LocationHandler: React.FC<{
  locationEnabled?: boolean;
  setLocationEnabled: (enabled: boolean) => void;
}> = ({ locationEnabled = false, setLocationEnabled }) => {
  const map = useMap();
  const { showAlert } = useAlert();
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const watchId = React.useRef<number | null>(null);

  useEffect(() => {
    if (locationEnabled && map) {
      // Watch position for continuous updates
      if (navigator.geolocation) {
        const id = navigator.geolocation.watchPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            // Update the user location marker
            setUserLocation((prevLocation) => {
              // Only pan to location on the first position update
              if (!prevLocation) {
                map.panTo(location);
                map.setZoom(15);
              }
              return location;
            });
          },
          (error) => {
            console.error('Error getting user location:', error);
            // Handle different error types with user-friendly alerts
            switch (error.code) {
              case error.PERMISSION_DENIED:
                showAlert(
                  'Location access denied. Please enable location permissions to use this feature.',
                  'warning'
                );
                setLocationEnabled(false);
                break;
              case error.POSITION_UNAVAILABLE:
                showAlert(
                  'Location information is currently unavailable.',
                  'warning'
                );
                setLocationEnabled(false);
                break;
              case error.TIMEOUT:
                showAlert(
                  'Location request timed out. Please try again.',
                  'warning'
                );
                break;
              default:
                showAlert(
                  'An error occurred while getting your location.',
                  'error'
                );
                setLocationEnabled(false);
                break;
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 30000, // Use cached position for up to 30 seconds for better performance
          }
        );
        watchId.current = id;
      } else {
        showAlert('Geolocation is not supported by this browser.', 'error');
      }
    } else if (!locationEnabled) {
      // Clear the user location marker when location is disabled
      setUserLocation(null);

      // Stop watching position
      if (watchId.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
    }

    // Cleanup function to stop watching when component unmounts
    return () => {
      if (watchId.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
  }, [locationEnabled, map, setLocationEnabled, showAlert]);

  // Render the user location marker if we have a location and location is enabled
  if (userLocation && locationEnabled) {
    return (
      <AdvancedMarker position={userLocation} title="Your Location">
        <div
          style={{
            width: '20px',
            height: '20px',
            backgroundColor: '#4285F4',
            border: '3px solid white',
            borderRadius: '50%',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          }}
        />
      </AdvancedMarker>
    );
  }

  return null;
};
const InnerGoogleMapElement: React.FC<{
  mapOptions: MapProps;
  children: ReactNode;
  locationEnabled?: boolean;
  setLocationEnabled: (enabled: boolean) => void;
}> = ({ mapOptions, children, locationEnabled, setLocationEnabled }) => {
  const {
    selectedMarker,
    selectedIcon,
    setSelectedMapIcon,
    setTilesLoaded,
    mapMessages,
    removeMapMessage,
  } = useMapContext();
  const {
    modules,
    layerControlOpen,
    toggleLayerControl,
    getStore,
    getModuleSlice,
  } = useModules();
  const theme = useTheme();
  const mapMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  theme.palette.mode === 'dark'
    ? (defaultMapOptions.mapId = import.meta.env.VITE_MAP_ID_DARK)
    : (defaultMapOptions.mapId = import.meta.env.VITE_MAP_ID_LIGHT);

  const wmsLayers = useMemo(
    () =>
      modules.filter((module) => {
        const mapLayer = resolveWmsMapLayer(
          module.mapConfig.wmsMapLayer,
          mapMode
        );
        return !!mapLayer && '' !== mapLayer.server;
      }),
    [modules, mapMode]
  );

  // Get unique WMS server hosts to avoid creating duplicate GoogleWmsLayers components
  const wmsServerHosts = useMemo(() => {
    const uniqueHosts = new Set(
      wmsLayers
        .map(
          (layer) =>
            resolveWmsMapLayer(layer.mapConfig.wmsMapLayer, mapMode)?.server
        )
        .filter(Boolean)
    );
    return Array.from(uniqueHosts) as string[];
  }, [wmsLayers, mapMode]);

  const endpointMapModules = useMemo(
    () =>
      modules.filter(
        (module) =>
          module.mapConfig.showOnMap && module.apiConfig?.endpoint !== ''
      ),
    [modules]
  );

  const marqueeModules = useMemo(
    () => modules.filter((module) => module.displayConfig?.showMarquee),
    [modules]
  );

  const { domains } = useDomains();

  const disableWmsLayers = useMemo(() => {
    // Disable WMS layers in development mode or if domain setting is enabled
    return (
      domains.find((d) => d.name === 'extWebsite.wms.off')?.value === 'true' ||
      false
    );
  }, [domains]);

  // Collect all objects from marquee modules using direct Redux access
  // Use allObjects instead of displayObjects so marquee shows all events regardless of filters
  const [marqueeObjects, setMarqueeObjects] = useState<MapIcon[]>([]);

  // Subscribe to Redux changes to update marquee objects in real-time
  useEffect(() => {
    const store = getStore();
    if (!store) return;

    let debounceTimeout: NodeJS.Timeout;
    let lastMarqueeSignature = '';

    // Create a signature of marquee-relevant data for change detection
    const createMarqueeSignature = (objects: MapIcon[]) => {
      return objects
        .filter((obj) => {
          const anyObj = obj as any;
          return (
            (anyObj.specialAlert || anyObj.informationalAlert) &&
            anyObj.advisoryText?.trim()
          );
        })
        .map((obj) => {
          const anyObj = obj as any;
          return `${obj.id}:${anyObj.specialAlert}:${anyObj.informationalAlert}:${anyObj.advisoryText}`;
        })
        .sort()
        .join('|');
    };

    const updateMarqueeObjects = () => {
      // Clear any pending updates
      clearTimeout(debounceTimeout);

      // Debounce updates to prevent rapid state changes
      debounceTimeout = setTimeout(() => {
        const objects: MapIcon[] = [];
        marqueeModules.forEach((module) => {
          const moduleSlice = getModuleSlice(module.name);
          if (moduleSlice) {
            const moduleObjects = moduleSlice.selectors.selectAllObjects(
              store.getState()
            );
            if (moduleObjects && Array.isArray(moduleObjects)) {
              objects.push(...moduleObjects);
            }
          }
        });

        // Create signature of current marquee-relevant data
        const currentSignature = createMarqueeSignature(objects);

        // Only update if the marquee-relevant data actually changed
        if (currentSignature !== lastMarqueeSignature) {
          lastMarqueeSignature = currentSignature;
          setMarqueeObjects(objects);
        }
      }, 100); // 100ms debounce
    };

    // Initial load
    updateMarqueeObjects();

    // Subscribe to store changes
    const unsubscribe = store.subscribe(updateMarqueeObjects);

    return () => {
      clearTimeout(debounceTimeout);
      unsubscribe();
    };
  }, [marqueeModules, getStore, getModuleSlice]);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapHeight, setMapHeight] = useState<number>(0);

  // Measure the map container height using ResizeObserver
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMapHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
      }}
      style={
        {
          '--map-container-height': `${mapHeight}px`,
        } as React.CSSProperties
      }
    >
      {marqueeObjects.length > 0 && (
        <Box sx={{ flexShrink: 0 }}>
          <MapMarquee objects={marqueeObjects} />
        </Box>
      )}
      <Box
        ref={mapContainerRef}
        sx={{ flexGrow: 1, minHeight: 0, height: '100%' }}
      >
        <Map
          {...{
            ...defaultMapOptions,
            ...mapOptions,
          }}
          onClick={() => {
            setSelectedMapIcon(null, null);
            if (layerControlOpen) {
              toggleLayerControl();
            }
          }}
          onTilesLoaded={() => {
            setTilesLoaded(true);
          }}
        />
        {children}
        <LocationHandler
          locationEnabled={locationEnabled}
          setLocationEnabled={setLocationEnabled}
        />
        <MapMessage
          messages={mapMessages}
          onClose={() => {
            // Clear all messages when user closes
            mapMessages.forEach((msg) => removeMapMessage(msg));
          }}
        />
      </Box>
      {endpointMapModules.map((module) => {
        return <GoogleEndpointLayer key={module.name} module={module} />;
      })}
      {selectedMarker && selectedIcon && <GoogleInfoWindow />}
      <GoogleTrafficLayer />
      {!disableWmsLayers &&
        wmsServerHosts.map((serverHost) => {
          return (
            <GoogleWmsLayers
              key={`${serverHost}_${mapMode}`}
              serverHost={serverHost}
              mode={mapMode}
            />
          );
        })}
    </Box>
  );
};

export default GoogleMapElement;
